import React, { useState, useMemo } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandGroup, CommandItem } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown, Package, X, Plus } from "lucide-react";

/**
 * Generic searchable, scrollable single-select combobox.
 * Mirrors the CategoryCombobox pattern (Popover + cmdk) which is iPhone
 * touch-scroll safe via overscroll containment.
 *
 * Props:
 *  - value: current selected value (string)
 *  - onChange: (value) => void
 *  - options: [{ value, label, sublabel?, keywords?, image?, trailing? }]
 *  - placeholder / searchPlaceholder / emptyText
 *  - testId: base data-testid
 *  - showImages: render option thumbnails (products)
 *  - allowClear: show a "clear" row that emits ""
 *  - disabled, align, className
 */
export function SearchableSelect({
  value,
  onChange,
  options = [],
  placeholder = "Pilih...",
  searchPlaceholder = "Cari...",
  emptyText = "Tidak ditemukan",
  testId = "searchable-select",
  showImages = false,
  allowClear = false,
  disabled = false,
  align = "start",
  className = "",
  maxResults = 0,
  onCreateNew = null,
  createLabel = "Tambah baru",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selected = options.find((o) => o.value === value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = options;
    if (q) {
      list = options.filter((o) => {
        const hay = [o.label, o.sublabel, ...(o.keywords || [])]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      });
      // best matches first: label starts-with, then label contains, then the rest
      list = list.slice().sort((a, b) => {
        const la = (a.label || "").toLowerCase(), lb = (b.label || "").toLowerCase();
        const ra = la.startsWith(q) ? 0 : la.includes(q) ? 1 : 2;
        const rb = lb.startsWith(q) ? 0 : lb.includes(q) ? 1 : 2;
        return ra - rb;
      });
    }
    return maxResults > 0 ? list.slice(0, maxResults) : list;
  }, [query, options, maxResults]);

  const pick = (v) => {
    onChange(v);
    setOpen(false);
    setQuery("");
  };

  const createNew = () => {
    const q = query.trim();
    setOpen(false);
    setQuery("");
    onCreateNew && onCreateNew(q);
  };

  return (
    <Popover modal open={open} onOpenChange={(v) => { setOpen(v); if (!v) setQuery(""); }}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={`w-full justify-between font-normal ${className}`}
          data-testid={testId}
        >
          <span className={`flex items-center gap-2 min-w-0 ${selected ? "" : "text-muted-foreground"}`}>
            {showImages && (
              <span className="h-5 w-5 rounded bg-secondary flex items-center justify-center overflow-hidden shrink-0">
                {selected?.image ? (
                  <img src={selected.image} alt="" className="h-full w-full object-cover" />
                ) : (
                  <Package className="h-3 w-3 text-muted-foreground" />
                )}
              </span>
            )}
            <span className="truncate">{selected ? selected.label : placeholder}</span>
          </span>
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[240px]"
        align={align}
        sideOffset={6}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={query}
            onValueChange={setQuery}
            data-testid={`${testId}-input`}
          />
          <CommandList className="max-h-64 overflow-y-auto overscroll-contain" data-testid={`${testId}-list`}>
            {filtered.length === 0 && (
              <div className="py-6 text-center text-sm text-muted-foreground">{emptyText}</div>
            )}
            <CommandGroup>
              {allowClear && value && (
                <CommandItem value="__clear__" onSelect={() => pick("")} data-testid={`${testId}-clear`}>
                  <X className="h-3.5 w-3.5 mr-2 opacity-50" />Kosongkan
                </CommandItem>
              )}
              {filtered.map((o) => (
                <CommandItem
                  key={o.value}
                  value={o.value}
                  onSelect={() => pick(o.value)}
                  data-testid={`${testId}-option`}
                  className="gap-2 items-center"
                >
                  <Check className={`h-4 w-4 shrink-0 ${value === o.value ? "opacity-100" : "opacity-0"}`} />
                  {showImages && (
                    <span className="h-8 w-8 rounded-md bg-secondary flex items-center justify-center overflow-hidden shrink-0">
                      {o.image ? (
                        <img src={o.image} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <Package className="h-4 w-4 text-muted-foreground" />
                      )}
                    </span>
                  )}
                  <span className="flex-1 min-w-0">
                    <span className="block truncate text-sm">{o.label}</span>
                    {o.sublabel && (
                      <span className="block truncate text-[11px] text-muted-foreground">{o.sublabel}</span>
                    )}
                  </span>
                  {o.trailing && (
                    <span className="text-xs text-muted-foreground shrink-0">{o.trailing}</span>
                  )}
                </CommandItem>
              ))}
              {onCreateNew && (
                <CommandItem value="__create__" onSelect={createNew} data-testid={`${testId}-create`}
                  className="gap-2 items-center text-primary border-t border-border mt-1 pt-2">
                  <Plus className="h-4 w-4 shrink-0" />
                  <span className="text-sm font-medium truncate">{createLabel}{query.trim() ? ` "${query.trim()}"` : ""}</span>
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
